import classNames from 'classnames'
import { useRef } from 'react'
import { useIntl } from 'react-intl'
import { TextField, Typography } from 'src/modules/core/components'
import { BRANDING_SWATCHES } from '../booking-branding-card.domain'
import type { BrandingAccentPickerProps } from './branding-accent-picker.interface'
import classes from './branding-accent-picker.module.scss'

const LABEL_ID = 'booking-branding-accent-label'
const HINT_ID = 'booking-branding-accent-hint'
const ERROR_ID = 'booking-branding-accent-error'

export function BrandingAccentPicker({
  inputValue,
  selectedSwatch,
  pickerColor,
  showError,
  disabled,
  onSelect,
  onChange,
  onBlur,
}: BrandingAccentPickerProps) {
  const { formatMessage } = useIntl()
  const nativePicker = useRef<HTMLInputElement>(null)
  const customColorLabel = formatMessage({ id: 'settings.bookingBranding.accent.customColor' })

  return (
    <div className={classes.root}>
      <Typography variant="body" className={classes.label} id={LABEL_ID}>
        {formatMessage({ id: 'settings.bookingBranding.accent.label' })}
      </Typography>
      <div className={classes.swatches} role="group" aria-labelledby={LABEL_ID}>
        {BRANDING_SWATCHES.map((hex) => (
          <button
            key={hex}
            type="button"
            className={classNames(classes.swatch, { [classes.swatchSelected]: hex === selectedSwatch })}
            style={{ backgroundColor: hex }}
            aria-label={hex}
            aria-pressed={hex === selectedSwatch}
            disabled={disabled}
            onClick={() => onSelect(hex)}
          />
        ))}
      </div>
      <div className={classes.hexField}>
        <TextField
          className={classes.hexInput}
          value={inputValue}
          aria-labelledby={LABEL_ID}
          aria-describedby={showError ? `${HINT_ID} ${ERROR_ID}` : HINT_ID}
          aria-invalid={showError}
          hint={formatMessage({ id: 'settings.bookingBranding.accent.hint' })}
          hintId={HINT_ID}
          error={showError ? formatMessage({ id: 'settings.bookingBranding.accent.invalidHex' }) : undefined}
          errorId={ERROR_ID}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
        />
        <button
          type="button"
          className={classes.colorChip}
          style={{ backgroundColor: pickerColor }}
          aria-label={customColorLabel}
          title={customColorLabel}
          disabled={disabled}
          onClick={() => openNativePicker(nativePicker.current)}
        />
        <input
          ref={nativePicker}
          type="color"
          className={classes.nativePicker}
          value={pickerColor.toLowerCase()}
          aria-hidden="true"
          tabIndex={-1}
          disabled={disabled}
          data-testid="branding-accent-native-picker"
          onChange={(event) => onSelect(event.target.value.toUpperCase())}
        />
      </div>
    </div>
  )
}

function openNativePicker(input: HTMLInputElement | null) {
  if (typeof input?.showPicker !== 'function') return
  try {
    input.showPicker()
  } catch {
    // Throws without a user gesture or where the picker is blocked; the field stays a plain hex input.
  }
}
